'use client';

import { useTranslations } from 'next-intl';
import {
  Battery,
  BoxOutline,
  CaretUp,
  Charger,
  Checkmark,
  ChevronRight,
  CircleTick,
  HandWithRing,
  InfoIcon,
  Return,
  ShippingBox,
  ShoppingTrolley,
  ArrowTopRight,
  WarningTriangle,
} from 'assets/icons';
import { PrimaryButton } from 'components/shared/PrimaryButton';
import BirthdayIcon from 'assets/birthday-icon.svg';
import { analytics } from 'lib/analytics/analytics';
// import { DeliveryManagement } from 'components/m1/DeliveryManagement';
import { isMarketingBannerActive } from 'components/shared/Header/MarketingBanner';
import { UhxPricingModal } from 'components/shared/InfoModals/uhx';
import { OffersModalNewProvider } from 'components/shared/OffersModal';
// import { PlantTreesBanner } from 'components/shared/PlantTreesBanner';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  forwardRef,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AfibShopifyMap,
  hasAfibVariants,
} from 'content/products/addons/afib';
import { CardioAdaptibilityShopifyMap } from 'content/products/addons/cardioAdaptibility';
import { CnoProShopifyMap } from 'content/products/addons/cnoPro';
import {
  ClueShopifyMap,
  isClueAvailable,
} from 'content/products/addons/clue';
import { RespiratoryShopifyMap } from 'content/products/addons/respiratory';
import { UhxShopifyMap } from 'content/products/uhx';
import { CurrencySymbol, useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import useIsCnOPlus from 'hooks/pricing/useIsCnOPlus';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import {
  CustomImage,
  useModal,
  getShippingAndTaxDisclaimerMap,
  FSAHSAComponent,
  DotSpinner,
} from '../../..';
import styles from './index.module.css';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { OffersCarousel, OffersDataType } from '../OffersCarousel';
import {
  DieselRingSelectionContext,
  DieselRingSelectionContext as RingSelectionContext,
} from '../DieselRingSelectionContext';
import { SecondaryOfferDataType } from '../SecondaryOfferComponent';
import { H2 } from '../styleConstants';
import { Carousal } from './Carousal';
import { PrimaryOffers, PrimaryOffersTransitionTile } from './PrimaryOffers';
import { HeroCategoriesMap } from 'components/shared/Cart/utils';
import { Currency } from 'utils/Currency';
import Image from 'next/image';
import { CnoProPricingModal } from 'components/shared/InfoModals/CnoPro';
import { AfibPricingModal } from 'components/shared/InfoModals/Afib';
import { CardioAdaptabilityPricingModal } from 'components/shared/InfoModals/CardioAdaptability';
import { CluePricingModal } from 'components/shared/InfoModals/Clue';
import { RespiratoryPricingModal } from 'components/shared/InfoModals/Respiratory';
import { ClueLogo } from 'assets/icons/ClueLogo';
import { CircledArrow } from 'assets/icons/CircledArrow';
import { OtherProductSection } from './OtherProductsSection';
import {
  DEFAULT_PRICE_STRIKED_STORES,
  DieselRingCarouselData,
  DieselRingColorNameImages,
  DieselRingColors,
  DieselRingPriceData,
  DieselRingSizes,
  productMap_diesel_ring,
} from 'content/products/dieselRing';
import { AfibCountryCodes } from 'content/products/ring';
import CustomLink from 'components/shared/CustomLink';
import { RespiratoryLogo } from 'assets/icons/RespiratoryLogo';

export const PanelContainer = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={`u-grid${className ? ` ${className}` : ''}`} {...props} />
);

export const LeftPanel = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={className ? `${styles.leftPanel} ${className}` : styles.leftPanel}
    {...props}
  />
);

// Mobile-only product carousel slot. Rendered at all breakpoints so the SSR
// markup is correct without a JS width gate, and hidden on desktop via CSS
// (where the LeftPanel carousel takes over). Replaces the old
// `width < PHONE_MAX && ...` gate that caused the hero to pop in after hydration.
const MobileCarouselSection = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={
      className
        ? `${styles.mobileCarouselSection} ${className}`
        : styles.mobileCarouselSection
    }
    {...props}
  />
);

// Desktop/mobile primary-offers slots. Both rendered at all breakpoints so the
// SSR markup is correct without a JS width gate; CSS shows the matching one.
const DesktopPrimaryOffers = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={
      className
        ? `${styles.desktopPrimaryOffers} ${className}`
        : styles.desktopPrimaryOffers
    }
    {...props}
  />
);

const MobilePrimaryOffers = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={
      className
        ? `${styles.mobilePrimaryOffers} ${className}`
        : styles.mobilePrimaryOffers
    }
    {...props}
  />
);

export const RightPanel = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={
      className ? `${styles.rightPanel} ${className}` : styles.rightPanel
    }
    {...props}
  />
);

// Main Export Here 👇🏻
export const ProductSelectionSection = ({
  className,
  title,
  buySectionTitle,
  description,
  emiCopies,
  secondaryOfferData,
  offersData,
  defaultStrikedPrice,
  defaultOfferText,
  defaultOfferDiscount,
  bgColor,
  offersBackgroundColor,
  dealsBackgroundColor,
}: {
  className?: string;
  title?: string;
  buySectionTitle?: string;
  description?: string;
  emiCopies?: boolean;
  secondaryOfferData?: SecondaryOfferDataType[];
  offersData?: OffersDataType[];
  defaultStrikedPrice?: boolean;
  defaultOfferText?: string;
  defaultOfferDiscount?: string | number;
  bgColor?: string;
  offersBackgroundColor?: string;
  dealsBackgroundColor?: string;
}) => {
    const { width: _width } = useWindowSize();
    const { color, notServicable } = useContext(RingSelectionContext);
    const tDiesel = useTranslations('dieselRing');
    const tCommon = useTranslations('common');
    void title;

    return (
      <section
        className={`${styles.root} u-grid${className ? ` ${className}` : ''}`}
        style={{
          backgroundColor: bgColor ? bgColor : 'transparent',
          paddingTop: isMarketingBannerActive() ? '80px' : '48px',
        }}
      >
        <OffersModalNewProvider>
          <div className="content">
            {notServicable ? (
              <OtherProductSection
                title={tDiesel('buy.unavailableTitle')}
                subtitle={tCommon(
                  'productSelectionSection.otherProductSection.subtitle',
                )}
              />
            ) : null}
            {!notServicable && offersData && (
              <>
                <DesktopPrimaryOffers>
                  <PrimaryOffers
                    data={offersData}
                    $backgroundColor={offersBackgroundColor}
                    $dealsBackgroundColor={dealsBackgroundColor}
                  />
                </DesktopPrimaryOffers>
                <MobilePrimaryOffers>
                  <PrimaryOffersTransitionTile
                    data={offersData}
                    backgroundColor={offersBackgroundColor}
                    dealsBackgroundColor={dealsBackgroundColor}
                  />
                </MobilePrimaryOffers>
              </>
            )}
            <PanelContainer
              style={{ filter: notServicable ? 'grayscale(1)' : undefined }}
            >
              <LeftPanel>
                <StickyPanel>
                  <div>
                    <Carousal
                      // selectedColor={color as DieselRingColors}
                      items={
                        (color
                          ? DieselRingCarouselData[color as DieselRingColors]
                          : DieselRingCarouselData[DieselRingColors.CRYO]) ?? []
                      }
                    />
                  </div>
                </StickyPanel>
              </LeftPanel>
              <RightPanel>
                <BuySection
                  buySectionTitle={buySectionTitle}
                  description={description}
                  emiCopies={emiCopies}
                  secondaryOfferData={secondaryOfferData}
                  offersData={offersData}
                  defaultStrikedPrice={defaultStrikedPrice}
                  defaultOfferText={defaultOfferText}
                  defaultOfferDiscount={defaultOfferDiscount}
                />
              </RightPanel>
            </PanelContainer>
          </div>
        </OffersModalNewProvider>
      </section>
    );
};

const StickyPanel = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={
      className ? `${styles.stickyPanel} ${className}` : styles.stickyPanel
    }
    {...props}
  />
);

const SizeInfoModal = ({ className }: { className?: string }) => {
  const { width } = useWindowSize();
  return (
    <div
      className={
        className ? `${styles.sizeInfoModal} ${className}` : styles.sizeInfoModal
      }
    >
      {/* <h2>How Ring Sizing Works</h2> */}
      <h2>How Ring Sizing Works</h2>
      <p>
        Diesel Ultrahuman Ring uses custom sizes, distinct from standard smart
        ring and market sizes. A precise fit is essential for accurate data
        tracking and comfort.
        {/* {t('ring.productSelection.sizingSection.modalNew.subtitle')} */}
      </p>
      <ol>
        <li>
          <ShoppingTrolley />
          <h5>
            {/* <span>Step 1:</span> Order your ring */}
            <span>Step 1:</span> Order your ring
          </h5>
          <p>
            {/* Place your order for the ring. A complimentary sizing kit is
            automatically included with every purchase unless you specifically
            opt out of it. */}
            Place your order for the ring. A complimentary sizing kit is
            automatically included with every purchase unless you specifically
            opt out of it.
          </p>
        </li>
        <li>
          <BoxOutline fill="#000000" />
          <h5>
            {/* <span>Step 2:</span> Await the free ring sizing kit delivery */}
            <span>Step 2:</span> Await the free ring sizing kit delivery
          </h5>
          <p>
            {/* We expedite your sizing kit, ensuring it&apos;s shipped within 24
            hours after you place your order. The kit contains a range of sample
            rings to help determine your ideal fit. This kit is yours to keep
            and share. */}
            We expedite your sizing kit, ensuring it&apos;s shipped after you
            place your order. The kit contains a range of sample rings to help
            determine your ideal fit. This kit is yours to keep and share.
          </p>
        </li>
        <li>
          <HandWithRing />
          <h5>
            {/* <span>Step 3:</span> Find your perfect ring size */}
            <span>Step 3:</span> Find your perfect ring size
          </h5>
          <p>
            {/* Use the sample rings to identify your most comfortable size. For
            optimal results, watch the video guide or read the step-by-step
            guide{' '} */}
            Use the sample rings to identify your most comfortable size. For
            optimal results, watch the video guide or read the step-by-step
            guide{' '}
            <CustomLink
              target={'_blank'}
              href="/sizing"
              style={{ textDecoration: 'underline' }}
            >
              {/* here */}
              here{' '}
            </CustomLink>
            . {/* Take time to ensure the best fit. */}
            Take time to ensure the best fit.
          </p>
        </li>
        <li>
          <CircleTick />
          <h5>
            {/* <span>Step 4:</span> Submit your ring size */}
            <span>Step 4:</span> Submit your ring size
          </h5>
          <p>
            {/* After confirming your ideal size, submit it through the Ultrahuman
            app or via the order tracking link. We&apos;ll then process your
            order and ship your ring. */}
            After confirming your ideal size, submit it through the Ultrahuman
            app or via the order tracking link. We&apos;ll then process your
            order and ship your ring.
          </p>
        </li>
      </ol>
      <CustomLink
        className="video"
        target="_blank"
        href="https://www.youtube.com/watch?v=t9ca-Lq70q4"
      >
        <CustomImage
          width={540}
          height={270}
          alt="Sizing Kit Video"
          src={
            width < 900
              ? 'web_v2/ring-buy/thumbnail_desktop.png'
              : 'web_v2/ring-buy/thumbnail_mobile.png'
          }
        />
        Watch our video tutorial for quick guidance
      </CustomLink>
      <hr />

      <p className="disclaimer">
        {/* The right fit ensures both comfort for continuous wear and accurate data
        tracking. If you need any assistance, our team is here to help at every
        step. Reach out to us at{' '} */}
        The right fit ensures both comfort for continuous wear and accurate data
        tracking. If you need any assistance, our team is here to help at every
        step. Reach out to us at{' '}
        <a href="mailto:support@ultrahuman.com">support@ultrahuman.com</a>.
      </p>
    </div>
  );
};

const LearnMoreButton = ({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => {
    return (
      <button
        className={
          className
            ? `${styles.learnMoreButton} ${className}`
            : styles.learnMoreButton
        }
        {...props}
      >
        Learn more
        <CircledArrow />
      </button>
    );
};

const Section = forwardRef<
    HTMLDivElement,
    {
      className?: string;
      title?: React.ReactNode | string;
      subTitle?: React.ReactNode | string;
      subText?: React.ReactNode | string;
      openInfoModal?: () => void;
      openInfoModalText?: React.ReactNode | string;
      children: React.ReactNode;
      childClass: string;
      sectionClass?: string;
      show: boolean;
      disableScrollToScreen?: boolean;
      hsaFsaBadge?: boolean;
    }
  >(
    (
      {
        className,
        title,
        subTitle,
        subText,
        openInfoModal,
        openInfoModalText,
        children,
        childClass,
        sectionClass,
        show,
        hsaFsaBadge,
      },
      ref,
    ) => {
      return (
        <div
          ref={ref}
          className={`${styles.section}${className ? ` ${className}` : ''} ${
            show ? 'show' : ''
          }  ${sectionClass ?? ''}`}
          data-has-modal-text={openInfoModalText ? 'true' : undefined}
        >
          {title ? (
            <div className="section-row-title">
              <h5>
                {title} <span className="subtitle">{subTitle}</span>
              </h5>
              {openInfoModal ? (
                <button onClick={openInfoModal}>
                  {!openInfoModalText ? (
                    <InfoIcon
                      style={{
                        width: '1.6rem',
                        height: 'auto',
                        marginTop: '1px', //change this when changing the font sizes
                      }}
                      fill="#000"
                    />
                  ) : null}
                  {openInfoModalText ? openInfoModalText : null}
                </button>
              ) : null}
            </div>
          ) : null}
          {subText ? <p className="section-row-sub-title">{subText}</p> : null}
          {hsaFsaBadge ? (
            <div className="hsa-fsa-badge">
              <FSAHSAComponent />
            </div>
          ) : null}
          <div className={`section-row-content ${childClass}`}>{children}</div>
        </div>
      );
    },
  );

Section.displayName = 'Section';

const Separator = ({
  transparent,
  size = 'lg',
}: {
  transparent?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) => {
  const sizeMap = { sm: '16px', md: '24px', lg: '32px', xl: '48px' };
  return (
    <hr
      className={styles.separator}
      style={{
        margin: `${sizeMap[size]} 0`,
        background: transparent ? 'none' : 'initial',
        border: transparent ? 'none' : '0.5px solid rgba(0, 0, 0, 0.1)',
      }}
    />
  );
};

const PowerPlugSubsection = ({
  title,
  description,
  image,
  children,
  className,
}: {
  title: string;
  description?: string;
  image?: string;
  children: React.ReactNode;
  className?: string;
}) => {
    return (
      <div
        className={
          className
            ? `${styles.powerPlugSubsection} ${className}`
            : styles.powerPlugSubsection
        }
      >
        <div className="subsection-header">
          {image ? (
            <div className="image-container">
              <CustomImage src={image} alt={title} height="64" width="64" />
            </div>
          ) : null}
          <div className="text-container">
            <h4 className="subsection-title">{title}</h4>
            {description ? (
              <p className="subsection-description">{description}</p>
            ) : null}
          </div>
        </div>
        <div className="ultrahumanx-content">{children}</div>
      </div>
    );
};

const LargePromptBtnStyledContent = ({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={
      className ? `${styles.largePrompt} ${className}` : styles.largePrompt
    }
    {...props}
  />
);

const OutOfStockBanner = ({ className }: { className?: string }) => {
  const t = useTranslations('ring');
  return (
    <div
      className={
        className
          ? `${styles.outOfStockBanner} ${className}`
          : styles.outOfStockBanner
      }
    >
      <p>{t('ring.productSelection.cartSummary.currentlySoldOut')}</p>
    </div>
  );
};

const LimitedEditionBanner = ({ className }: { className?: string }) => {
  const { shippingEstimate } = useContext(DieselRingSelectionContext);

  return (
    <div
      className={
        className
          ? `${styles.limitedEditionBanner} ${className}`
          : styles.limitedEditionBanner
      }
    >
      <h6>{shippingEstimate}</h6>
      <p>
        The Diesel Ultrahuman Ring reflects precise craftsmanship. Its
        meticulous development means that it may take a little more time to
        arrive.
      </p>
    </div>
  );
};

const BuySection = ({
  className,
  emiCopies,
  secondaryOfferData,
  // offersData,
  // description,
  buySectionTitle = 'Diesel Ultrahuman Ring',
  defaultStrikedPrice = false,
  defaultOfferText,
  defaultOfferDiscount,
}: {
  className?: string;
  emiCopies?: boolean;
  description?: string;
  buySectionTitle?: string;
  secondaryOfferData?: SecondaryOfferDataType[];
  offersData?: OffersDataType[];
  defaultStrikedPrice?: boolean;
  defaultOfferText?: string;
  defaultOfferDiscount?: number | string;
}) => {
    const t = useTranslations('ring');
    const tCart = useTranslations('cart');

    const { region: country } = useRegionLocale();
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const RingSelectionClient = useContext(RingSelectionContext);
    const { store, getCampaignForProduct } = useCartStore(
      useShallow((s) => ({
        store: s.store,
        getCampaignForProduct: s.getCampaignForProduct,
      })),
    );
    const ringCampaign = getCampaignForProduct('ring');
    const { width: _width } = useWindowSize();
    const ModalClient = useModal();
    const {
      color,
      setColor,

      size,
      setSize,

      uhx,

      uhxCart,
      setUhxCart,

      defaultCurrency: currency,

      checkout,
      isLoading,
      price,
      effectivePrice,
      currencySymbol,
      ultraHumanPrice,
      shippingEstimate,
      affiliateDetails,
      affiliateDiscount,
      referral,
      sectionRefs,
      effectiveDiscount,
      afibCart,
      setAfibCart,
      cardioAdaptibilityCart,
      setCardioAdaptabilityCart,
      cnoProCart,
      setCnoProCart,
      clueCart,
      setClueCart,
      respiratoryCart,
      setRespiratoryCart,
      priceDrop,
      notServicable,
      notServicableMessage,
      outOfStock,
      isOpenSizeDisabled,
    } = RingSelectionClient;

    if (!defaultStrikedPrice) {
      defaultStrikedPrice = DEFAULT_PRICE_STRIKED_STORES.includes(
        store as ShopifyStore,
      );
    }

    const effectiveStoreForAffiliate = getEffectiveStoreForDiscount(store);

    const { showSizeRef, coverageRef, powerplugRef, cartSummaryRef } =
      sectionRefs;

    const isAfibOrCardio: 'afib' | 'cardio' =
      country &&
      AfibCountryCodes.includes(country) &&
      hasAfibVariants(store as ShopifyStore)
        ? 'afib'
        : 'cardio';

    const isClueFlow =
      searchParams?.get('flow') === 'clue' &&
      isClueAvailable(store as ShopifyStore);
    // DISABLED: Respiratory Health flow — forced off. Re-enable by restoring original expression.
    // const isRespiratoryFlow = searchParams?.get('flow') === 'respiratory';
    const isRespiratoryFlow = false;
    const { getValue } = useIsCnOPlus();
    const isOvusenseFlow = searchParams?.get('affiliateCode') === 'ovusense';
    const showRespiratoryAddon = false;

    // Determine ring promo text based on country
    // const ringPromoText =
    //   (router.query.locale as string)?.toUpperCase() === 'AU' ||
    //   country?.toUpperCase() === 'AU'
    //     ? 'Black Friday Offer Applied'
    //     : 'Special Offer Applied';

    //FOR HSA/FSA redirects
    useEffect(() => {
      const scrollTimeout = setTimeout(() => {
        if (
          powerplugRef?.current &&
          searchParams?.get('ps') === 'cardio-powerplug' &&
          store === ShopifyStore.US
        ) {
          powerplugRef.current.scrollIntoView({
            behavior: 'smooth',
          });
        }
      }, 100);

      return () => {
        if (scrollTimeout) clearTimeout(scrollTimeout);
      };
    }, []);

    const openSizeInfoModal = () => {
      ModalClient.set(<SizeInfoModal />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };

    const openUhxInfoModal = () => {
      ModalClient.set(<UhxPricingModal />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };

    // const openVoyagerCharger = () => {
    //   ModalClient.set(<TravelChargerPricingModal />);
    //   ModalClient.setPortal(true);
    //   ModalClient.show();
    // };

    const openCnoPro = () => {
      ModalClient.set(<CnoProPricingModal />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };

    const openAfibModal = () => {
      ModalClient.set(<AfibPricingModal />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };

    const openCardioModal = () => {
      ModalClient.set(<CardioAdaptabilityPricingModal />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };

    const openClueModal = () => {
      ModalClient.set(<CluePricingModal />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };

    const openRespiratoryModal = () => {
      ModalClient.set(<RespiratoryPricingModal />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };

    const handleCheckout = () => {
      if (isLoading) return;
      analytics.track(`CLICK: Product Selection, Add to Cart`);
      checkout();
      ModalClient.hide();
    };

    const [manualSizeSelectVisible, setManualSizeSelectVisible] = useState(
      isOpenSizeDisabled === true ? true : false,
    );

    const sizeContainerAccordionRef = useRef<HTMLDivElement>(null);
    const sizeContainerRef = useRef<HTMLDivElement>(null);

    const handleManualSizeSelectShow = () => {
      if (isOpenSizeDisabled) {
        setManualSizeSelectVisible(true);
        return;
      }
      setManualSizeSelectVisible((prev) => !prev);
    };

    const isCnoProActive = true;

    // this is used to scoll from powerplugs
    // const element = document.getElementById('powerplugs');
    // const scrolledRef = useRef<boolean>(false);
    // useEffect(() => {
    //   if (
    //     router.asPath.includes('#cardio') &&
    //     !scrolledRef.current &&
    //     element
    //   ) {
    //     coverageRef?.current?.scrollIntoView({ behavior: 'smooth' });
    //     scrolledRef.current = true;
    //   }
    // }, [router, element]);

    const sizeDisclaimerViewedRef = useRef<boolean>(false);

    const handleSizeDisclaimer = () => {
      if (sizeDisclaimerViewedRef.current || isOpenSizeDisabled) return;

      const DisclaimerButtons = ({
        className,
        ...props
      }: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
        <button
          className={
            className
              ? `${styles.disclaimerButton} ${className}`
              : styles.disclaimerButton
          }
          {...props}
        />
      );

      const DisclaimerButtonContainer = ({
        className,
        ...props
      }: React.HTMLAttributes<HTMLDivElement>) => (
        <div
          className={
            className
              ? `${styles.disclaimerButtonContainer} ${className}`
              : styles.disclaimerButtonContainer
          }
          {...props}
        />
      );

      ModalClient.setCloseBtnHidden(true);
      ModalClient.set(
        <div
          style={{
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
          }}
        >
          <h2
            style={{
              fontWeight: '500',
              fontSize: '24px',
              letterSpacing: '-0.3px',
            }}
          >
            <WarningTriangle
              fill="#F2B534"
              style={{
                height: '17px',
                width: 'auto',
                marginRight: '8px',
              }}
            />
            {/* Our ring sizes are unique */}
            Our ring sizes are unique
          </h2>
          <p
            style={{
              fontSize: '19px',
              marginTop: '16px',
              lineHeight: '130%',
              color: 'rgba(0,0,0,0.75)',
              maxWidth: '100%',
              letterSpacing: '-0.3px',
            }}
          >
            {/* Ultrahuman Rings have custom sizes, different from standard smart
            rings. The right fit ensures accurate data tracking. */}
            Ultrahuman Rings have custom sizes, different from standard smart
            rings. The right fit ensures accurate data tracking.
            <br />
            <br />
            {/* If you know your size, proceed confidently. If not, get a free
            sizing kit (shipped within 24 hours) for the perfect fit. Guessing
            wrong costs the planet. */}
            If you know your size, proceed confidently. If not, get a free
            sizing kit for the perfect fit. Guessing wrong costs the planet.
          </p>
          <DisclaimerButtonContainer>
            <DisclaimerButtons
              onClick={() => {
                setSize(DieselRingSizes.OPEN);
                ModalClient.hide();
                ModalClient.set(null);
                setManualSizeSelectVisible(false);
              }}
            >
              {/* Get free sizing kit */}
              Get free sizing kit
            </DisclaimerButtons>

            <DisclaimerButtons
              onClick={() => {
                sizeDisclaimerViewedRef.current = true;
                ModalClient.hide();
                ModalClient.set(null);
                analytics.track(`CLICK: Manual size selection`, {
                  size: size ?? null,
                });
              }}
              className="secondary gtm"
              data-buttontype="Manual Size Selection Agree"
              data-product="Manual Size Selection Agree"
            >
              {/* I know my size */}I know my size
            </DisclaimerButtons>
          </DisclaimerButtonContainer>
        </div>,
      );
      ModalClient.show();
    };

    const memoizedSizeContainerHeight = useMemo(() => {
      if (!sizeContainerAccordionRef.current || !sizeContainerRef.current)
        return '0px';
      if (!manualSizeSelectVisible) {
        return '0px';
      } else {
        return sizeContainerRef.current.scrollHeight + 24 + 'px';
      }
    }, [manualSizeSelectVisible, isOpenSizeDisabled]);
    const formatPriceWithComma = useFormatPrice();

    useEffect(() => {
      if (!sizeContainerAccordionRef.current) return;
      sizeContainerAccordionRef.current.style.height =
        memoizedSizeContainerHeight;
    }, [memoizedSizeContainerHeight]);

    useEffect(() => {
      if (
        searchParams?.get('affiliate') ||
        searchParams?.get('affiliateCode')
      ) {
        setUhxCart(null);
      }
    }, [searchParams]);

    const heroCategoryOfferTagMap = {
      [HeroCategoriesMap.ATHLETES]: (
        <>
          <CustomImage
            width={24}
            height={24}
            alt="athlete-icon"
            src={'web_v2/hero/hero-icons-green/Athlete.svg'}
          />{' '}
          Athlete Savings Applied
        </>
      ),
      [HeroCategoriesMap.SCIENTIST]: (
        <>
          <CustomImage
            width={24}
            height={24}
            alt="science-icon"
            src={'web_v2/hero/hero-icons-green/Science.svg'}
          />{' '}
          Science Savings Applied
        </>
      ),
      [HeroCategoriesMap.HEALTHCARE]: (
        <>
          <CustomImage
            width={24}
            height={24}
            alt="healthcare-icon"
            src={'web_v2/hero/hero-icons-green/Healthcare.svg'}
          />{' '}
          Healthcare Savings Applied
        </>
      ),
      [HeroCategoriesMap.EDUCATION]: (
        <>
          <CustomImage
            width={24}
            height={24}
            alt="university-icon"
            src={'web_v2/hero/hero-icons-green/University.svg'}
          />
          Education Savings Applied
        </>
      ),
      [HeroCategoriesMap.VETERANS]: (
        <>
          <CustomImage
            width={28}
            height={28}
            alt="veteran-icon"
            src={'web_v2/hero/hero-icons-green/Veteran.svg'}
          />{' '}
          Military Savings Applied
        </>
      ),
    };

    // const ColorSelectionChip = styled.div`
    //   display: flex;
    //   align-items: center;
    //   gap: 8px;
    //   padding: 8px 16px;
    //   // background: #f5f5f5;
    //   font-size: 14px;
    //   border-radius: 12px;
    //   color: rgba(0, 0, 0, 0.5);
    //   margin-top: 8px;

    //   strong {
    //     font-weight: 500;
    //     color: black;
    //   }
    // `;

    const PriceContentContainer = ({
      className,
      ...props
    }: React.HTMLAttributes<HTMLDivElement>) => (
      <div
        className={
          className
            ? `${styles.priceContentContainer} ${className}`
            : styles.priceContentContainer
        }
        {...props}
      />
    );

    const NotServicableMessageContainer = ({
      className,
      ...props
    }: React.HTMLAttributes<HTMLDivElement>) => (
      <div
        className={
          className
            ? `${styles.notServicableMessageContainer} ${className}`
            : styles.notServicableMessageContainer
        }
        {...props}
      />
    );

    const ringPriceForSelection =
      store && color ? DieselRingPriceData[store]?.[color] : undefined;
    const engravingStrikePrice = 0;
    const strikeBasePrice =
      (ringPriceForSelection?.striked_price ??
        ringPriceForSelection?.price ??
        0) + engravingStrikePrice;

    const NotServicableMessage = notServicable ? (
      notServicableMessage ? (
        <NotServicableMessageContainer>
          <WarningTriangle
            fill={'rgba(0, 0, 0, 0.6)'}
            style={{ width: '24px', height: '24px' }}
          />
          {notServicableMessage}
        </NotServicableMessageContainer>
      ) : (
        <NotServicableMessageContainer
          style={{
            justifyContent: 'center',
            fontWeight: '500',
            fontSize: '1.6rem',
            color: 'rgba(0, 0, 0, 0.6)',
            marginBottom: '24px',
          }}
        >
          Currently Unavailable
        </NotServicableMessageContainer>
      )
    ) : null;

    // Early return if store is not yet initialized
    if (!store) {
      return null;
    }

    const buildColorHref = (c: DieselRingColors) => {
      const p = new URLSearchParams(searchParams?.toString() ?? '');
      p.set('color', c);
      return `?${p.toString()}`;
    };
    // No history.replaceState here: prod's swatches are plain buttons with no URL
    // write, and a URL write per colour fires GA4's history-change page_view —
    // inflating the variant arm. Deep links still work: state inits from ?color=.
    const selectColor = (c: DieselRingColors) => {
      setColor(c);
    };

    const ColorSelectionSection = (
      <Section
        title={'Colour.'}
        subTitle={'Choose your favourite finish.'}
        childClass="color-section"
        show={true}
        disableScrollToScreen={true}
        sectionClass="color-section"
      >
        <>
          <div className="colors">
            {Object.values(DieselRingColors).map((element, index) => {
              // const tag = getRingAirColorTag(element as DieselRingColors, store);
              const tag = undefined;

              return (
                <a
                  href={buildColorHref(element as DieselRingColors)}
                  style={{
                    transitionDelay: `${index * 0.05}s`,
                    transitionProperty: 'opacity',
                    textDecoration: 'none',
                  }}
                  key={index}
                  className={`gtm color-swatch-btn ${element == color ? 'active' : ''}`}
                  data-testid={`ring-color-${element}`}
                  data-buttontype="ring color selector"
                  data-product="Diesel Ultrahuman Ring"
                  onClick={(e) => {
                    e.preventDefault();
                    selectColor(element as DieselRingColors);
                  }}
                >
                  {!notServicable && tag ? <b className="tag">{tag}</b> : null}
                  {notServicable === true ? (
                    <b className="tag">Currently Unavailable</b>
                  ) : null}
                  <CustomImage
                    width={72}
                    height={72}
                    src={DieselRingColorNameImages[element].image as string}
                    alt={element}
                  />
                  {DieselRingColorNameImages[element].name}
                </a>
              );
            })}
          </div>
          <div className="explore-links">
            <CustomLink href="/ring/buy">
              <button
                className="gtm explore-pill"
                data-buttontype="ring style selector"
                data-product="Diesel Ultrahuman Ring"
              >
                Explore Ring AIR <ArrowTopRight fill="#000" />
              </button>
            </CustomLink>
            <CustomLink href="/rare/buy">
              <button
                className="gtm explore-pill"
                data-buttontype="ring style selector"
                data-product="Diesel Ultrahuman Ring"
              >
                Explore Ring Rare <ArrowTopRight fill="#000" />
              </button>
            </CustomLink>
          </div>
        </>
      </Section>
    );

    const SizeSelectionSection = (
      <Section
        // title="Find your ring size"
        title={'Size.'}
        subTitle={'Sizing kit recommended for best fit.'}
        // subText="Guessing wrong costs the planet. Use the sizing kit to get it right the first time."

        childClass="sizing-section"
        openInfoModal={openSizeInfoModal}
        // openInfoModalText="How it works"
        // openInfoModalText={
        //   {t('ring.productSelection.sizingSection.openInfoModalText')}
        // }
        show={!!color}
        disableScrollToScreen={true}
        sectionClass="size-section"
        ref={showSizeRef}
      >
        <>
          <LargePromptBtnStyledContent
            className={`sizing-kit gtm pb ${
              size == DieselRingSizes.OPEN ? 'active' : ''
            }`}
            data-buttontype="ring size selector"
            data-testid="ring-size-open"
            data-product="Diesel Ultrahuman Ring"
            onClick={() => {
              setSize(DieselRingSizes.OPEN);
              setManualSizeSelectVisible(false);
            }}
            disabled={isOpenSizeDisabled}
          >
            <div className="content-wrapper">
              <CustomImage
                src={'web_v2/ring-buy/sizing-kit-1.png'}
                alt={'Sizing Kit'}
                width="64"
                height="64"
                loading="lazy"
                className="sizing-kit-img"
              />
              <div className="content">
                <div className="title-bar mb">
                  <div className="title">
                    {/* Sizing Kit (ships in 24 hours) */}
                    Sizing Kit{' '}
                    {/* (
                      {t('ring.productSelection.sizingSection.sizingKitNew.titleParenthesis')}
                      ) */}
                  </div>
                  <div className="disclaimer">
                    <div className="price">
                      {/* FREE */}
                      {isOpenSizeDisabled ? 'UNAVAILABLE' : 'FREE'}
                    </div>
                  </div>
                </div>
                <p className="sub-title">
                  Choose the right size with the ring samples, ensuring a
                  perfect fit for the best data accuracy.
                  {/* Choose the right size with the ring samples, ensuring a
                    perfect fit for the best data accuracy. */}
                </p>
                <div className="list">
                  <p>
                    <Checkmark className="checkmark" fill="#000000" />
                    Perfect ring fit—no guesswork
                  </p>
                  <p>
                    <Checkmark className="checkmark" fill="#000000" />
                    Yours to keep and share—no returns required
                  </p>
                  <p>
                    <Checkmark className="checkmark" fill="#000000" />
                    Eco-friendly materials—better for you and the planet
                  </p>
                </div>
              </div>
            </div>
          </LargePromptBtnStyledContent>

          {/* CONFIG: disable delivery management */}
          {/* {store === ShopifyStore.IN ? (
              <div
                className={`delivery-management-section ${
                  size == RingSizes.OPEN ? 'active' : ''
                }`}
                style={{
                  opacity:
                    RingSelectionClient.size === RingSizes.OPEN ? '1' : '0.5',
                  pointerEvents:
                    RingSelectionClient.size === RingSizes.OPEN
                      ? 'auto'
                      : 'none',
                }}
              >
                <DeliveryManagement />
              </div>
            ) : null} */}
          {/* <div className="divider">OR</div> */}
          {isOpenSizeDisabled ? null : (
            <button
              className="manual-size gtm"
              onClick={handleManualSizeSelectShow}
              data-buttontype="Ring Size Manual Show"
              data-product="Ring Size Manual Show"
            >
              {/* I know my ring size{' '} */}I have a ring sizing kit{' '}
              <CaretUp
                fill="#000000"
                style={{
                  transition: 'all 0.3s ease-in-out',
                  transform: manualSizeSelectVisible
                    ? 'rotate(0deg) translateX(0px)'
                    : 'rotate(180deg) translateX(1px)',
                  height: '0.8rem',
                }}
              />
            </button>
          )}
          <div
            className={`manual-size-select-container `}
            ref={sizeContainerAccordionRef}
            style={{
              height:
                isOpenSizeDisabled && sizeContainerRef.current
                  ? sizeContainerRef.current.scrollHeight + 24 + 'px'
                  : undefined,
            }}
          >
            <div className="" ref={sizeContainerRef}>
              {notServicable !== true && color ? (
                <div className="size-shipping">
                  <ShippingBox />
                  <p>{shippingEstimate}</p>
                </div>
              ) : null}
              {isOpenSizeDisabled ? null : (
                <div className="size-shipping">
                  <p>
                    {/* Not a 100% sure? Go for the sizing kit. Nailing the fit the
                    first time helps you and the Earth. */}
                    Not a 100% sure? Go for the sizing kit. Nailing the fit the
                    first time helps you and the Earth.
                  </p>
                </div>
              )}
              <div className="sizes">
                {Object.values(DieselRingSizes)
                  .filter(
                    (value) =>
                      typeof value === 'number' &&
                      value !== DieselRingSizes.OPEN,
                  )
                  .map((element, index) => {
                    return (
                      <button
                        style={{
                          transitionDelay: `${index * 0.05}s`,
                          transitionProperty: 'opacity',
                        }}
                        key={index}
                        className={`gtm ${size == element ? 'active' : ''}`}
                        data-buttontype="ring size selector"
                        data-product="Diesel Ultrahuman Ring"
                        data-testid={`ring-size-${element}`}
                        disabled={
                          color &&
                          productMap_diesel_ring[color as DieselRingColors]?.[
                            element as DieselRingSizes
                          ]?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                            ? false
                            : true
                        }
                        onClick={() => {
                          if (isOpenSizeDisabled) {
                            handleSizeDisclaimer();
                          }
                          setSize(element as DieselRingSizes);
                        }}
                      >
                        {element}
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>
        </>
      </Section>
    );

    const ClueAddonSection = isClueFlow ? (
      <Section
        title={'Clue Plus.'}
        subTitle={'Advanced cycle insights.'}
        ref={powerplugRef}
        childClass="powerPlugs-section"
        show={!!size}
        // subText={
        //   {t('ring.productSelection.powerPlugsSection.subText')}
        // }
      >
        <PowerPlugSubsection
          title={'Clue Plus'}
          description={
            "Take charge of your cycle health, whether you're trying to conceive, navigating perimenopause or just curious about your body's patterns."
          }
          image="web_v2/powerplugs/icons/clue.png"
        >
          <LargePromptBtnStyledContent
            className={`clue-addon gtm ${
              clueCart === 'Year1' ? 'active' : ''
            } paddingTop`}
            data-buttontype="clue addon"
            data-product="Diesel Ultrahuman Ring"
            data-testid={`ring-clue-1-year`}
            onClick={() => {
              setClueCart('Year1');
            }}
            disabled={
              size &&
              productMap_diesel_ring[color as DieselRingColors]?.[size]
                ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                ? false
                : true
            }
          >
            <div className="label">exclusive</div>
            <div className="content-wrapper">
              <div className="content">
                <div className="title-bar">
                  <div className="title">
                    <span>
                      <strong>1 Year</strong>
                    </span>
                  </div>
                  <div className="disclaimer">
                    <div className="price-display">
                      <span className="striked price">
                        {currencySymbol}
                        {formatPriceWithComma(
                          ClueShopifyMap[store as ShopifyStore]?.Year1?.price ||
                            0,
                        )}
                      </span>
                      <div className="price">
                        {currencySymbol}
                        {(() => {
                          const price =
                            ClueShopifyMap[store as ShopifyStore]?.Year1
                              ?.discountedPrice || 0;
                          const priceStr = price.toFixed(2);
                          const [whole, decimal] = priceStr.split('.');
                          const showDecimal = decimal !== '00';
                          return (
                            <>
                              {formatPriceWithComma(parseInt(whole))}
                              {showDecimal && (
                                <span
                                  style={{
                                    fontSize: '1.2rem',
                                    fontWeight: '400',
                                    color: '#000',
                                    letterSpacing: '-0.48px',
                                  }}
                                >
                                  .{decimal}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                {store === ShopifyStore.US ? (
                  <div className="hsa-fsa-badge">
                    <FSAHSAComponent />
                  </div>
                ) : null}
              </div>
            </div>
          </LargePromptBtnStyledContent>

          <button
            className={`no-ultrahumanx gtm ${
              clueCart === false ? 'active' : ''
            }`}
            data-buttontype="cno-pro selection"
            data-product="Diesel Ultrahuman Ring"
            onClick={() => {
              setClueCart(false);
            }}
            disabled={
              size &&
              productMap_diesel_ring[color as DieselRingColors]?.[size]
                ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                ? false
                : true
            }
          >
            <span>
              <strong>I don&apos;t want advanced cycle insights</strong>
            </span>
          </button>

          <LearnMoreButton onClick={openClueModal}>
            Learn more <CircledArrow />
          </LearnMoreButton>
        </PowerPlugSubsection>
      </Section>
    ) : null;

    const RespiratoryAddon = () => {
      return (
        <PowerPlugSubsection
          title={'Respiratory Health PowerPlug'}
          description={
            "See what's really happening while you sleep. Track snoring disruptions, and understand how they affect your HR, HRV, and recovery. All audio processing happens entirely on your device."
          }
          image="web_v2/powerplugs/icons/respiratory-3.png"
        >
          <LargePromptBtnStyledContent
            className={`respiratory-addon gtm ${
              respiratoryCart === 'Year1' ? 'active' : ''
            } paddingTop`}
            data-buttontype="respiratory addon"
            data-product="Diesel Ultrahuman Ring"
            data-testid={`ring-respiratory-1-year`}
            onClick={() => {
              setRespiratoryCart('Year1');
            }}
            disabled={
              size &&
              productMap_diesel_ring[color as DieselRingColors]?.[size]
                ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                ? false
                : true
            }
          >
            <div className="label">Limited Offer</div>
            <div className="content-wrapper">
              <div className="content">
                <div className="title-bar">
                  <div className="title">
                    <span>
                      <strong>1 Year Membership</strong>
                    </span>
                  </div>
                  <div className="disclaimer">
                    <div className="price-display">
                      <span
                        className={`price ${
                          isRespiratoryFlow ? 'striked' : ''
                        }`}
                      >
                        {currencySymbol}
                        {formatPriceWithComma(
                          RespiratoryShopifyMap[store as ShopifyStore]?.Year1
                            ?.price || 0,
                        )}
                      </span>
                      {isRespiratoryFlow ? (
                        <div className="price">
                          <span
                            style={{
                              fontSize: '1.6rem',
                              fontWeight: '500',
                              color: '#000',
                            }}
                          >
                            FREE
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                {store === ShopifyStore.US ? (
                  <div className="hsa-fsa-badge">
                    <FSAHSAComponent />
                  </div>
                ) : null}
              </div>
            </div>
          </LargePromptBtnStyledContent>

          <button
            className={`no-ultrahumanx gtm ${
              respiratoryCart === false ? 'active' : ''
            }`}
            data-buttontype="respiratory selection"
            data-product="Diesel Ultrahuman Ring"
            onClick={() => {
              setRespiratoryCart(false);
            }}
            disabled={
              size &&
              productMap_diesel_ring[color as DieselRingColors]?.[size]
                ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                ? false
                : true
            }
          >
            <span>
              <strong>I don&apos;t want advanced respiratory insights</strong>
            </span>
          </button>

          <LearnMoreButton onClick={openRespiratoryModal}>
            Learn more <CircledArrow />
          </LearnMoreButton>
        </PowerPlugSubsection>
      );
    };

    const PowerplugsSelectionSection = (
      <Section
        title={'Software plug-ins.'}
        subTitle={'For targeted health goals.'}
        ref={powerplugRef}
        childClass="powerPlugs-section"
        show={!!size}
        // subText={
        //   {t('ring.productSelection.powerPlugsSection.subText')}
        // }
      >
        <Fragment>
          {showRespiratoryAddon ? (
            <>
              <RespiratoryAddon />
              <Separator />
            </>
          ) : isCnoProActive && !isOvusenseFlow ? (
            <Fragment>
              {/* Women's Health */}
              <PowerPlugSubsection
                title={getValue(
                  'Cycle & Ovulation Pro PowerPlug',
                  'Cycle & Ovulation Plus PowerPlug',
                )}
                description={getValue(
                  'Clinically-validated cycle and ovulation insights, built for all types of cycles.',
                  'Science-informed cycle and ovulation-related insights, designed for all types of cycles.',
                )}
                image="web_v2/powerplugs/icons/cno-pro.png"
              >
                <LargePromptBtnStyledContent
                  className={`ultrahumanx gtm ${
                    cnoProCart && cnoProCart === 'Year1' ? 'active' : ''
                  }`}
                  data-buttontype="cno-pro"
                  data-product="Diesel Ultrahuman Ring"
                  data-testid={`ring-cnopro-1-year`}
                  onClick={() => {
                    setCnoProCart('Year1');
                  }}
                  disabled={
                    size &&
                    productMap_diesel_ring[color as DieselRingColors]?.[size]
                      ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                      ? false
                      : true
                  }
                >
                  <div className="content-wrapper">
                    <div className="content">
                      <div className="title-bar">
                        <div className="title">
                          <span>
                            <strong>1 Year</strong>
                          </span>
                          {store === ShopifyStore.US ? (
                            <div className="hsa-fsa-badge">
                              <FSAHSAComponent />
                            </div>
                          ) : null}
                        </div>
                        <div className="disclaimer">
                          <div className="price">
                            {currencySymbol}
                            {formatPriceWithComma(
                              CnoProShopifyMap[store as ShopifyStore]?.Year1
                                ?.price || 0,
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </LargePromptBtnStyledContent>
                <LargePromptBtnStyledContent
                  className={`ultrahumanx gtm ${
                    cnoProCart && cnoProCart === 'Year2' ? 'active' : ''
                  } ${
                    CnoProShopifyMap[store as ShopifyStore]?.Year2?.save
                      ? 'paddingTop'
                      : ''
                  }`}
                  data-buttontype="cno-pro"
                  data-product="Diesel Ultrahuman Ring"
                  data-testid={`ring-cnopro-2-year`}
                  onClick={() => {
                    setCnoProCart('Year2');
                  }}
                  disabled={
                    size &&
                    productMap_diesel_ring[color as DieselRingColors]?.[size]
                      ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                      ? false
                      : true
                  }
                >
                  {!!CnoProShopifyMap[store as ShopifyStore]?.Year2?.save && (
                    <div className="label">recommended</div>
                  )}

                  <div className="content-wrapper">
                    <div className="content">
                      <div className="title-bar">
                        <div className="title">
                          <span>
                            <strong>2 Years</strong>
                          </span>
                          {!!CnoProShopifyMap[store as ShopifyStore]?.Year2
                            ?.save && (
                            <span className="save-tag">
                              Save{' '}
                              {CnoProShopifyMap[store as ShopifyStore]?.Year2
                                ?.save || 15}
                              %
                            </span>
                          )}
                        </div>
                        <div className="disclaimer">
                          <div className="price">
                            {currencySymbol}
                            {formatPriceWithComma(
                              CnoProShopifyMap[store as ShopifyStore]?.Year2
                                ?.price || 0,
                            )}
                          </div>
                          {!!CnoProShopifyMap[store as ShopifyStore]?.Year2
                            ?.save && (
                            <div className="disclaimer-text">
                              <span>
                                {/* (That's just {currencySymbol} */}
                                (That&apos;s just {currencySymbol}
                                {formatPriceWithComma(
                                  parseFloat(
                                    (
                                      CnoProShopifyMap[store as ShopifyStore]
                                        ?.Year2.price / 2
                                    ).toFixed(2),
                                  ),
                                )}
                                /year)
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </LargePromptBtnStyledContent>
                <button
                  className={`no-ultrahumanx gtm ${
                    cnoProCart === false ? 'active' : ''
                  }`}
                  data-buttontype="cno-pro selection"
                  data-product="Diesel Ultrahuman Ring"
                  onClick={() => {
                    setCnoProCart(false);
                  }}
                  disabled={
                    size &&
                    productMap_diesel_ring[color as DieselRingColors]?.[size]
                      ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                      ? false
                      : true
                  }
                >
                  <span>
                    <strong>
                      {/* {t('ring.productSelection.cnoProSection.no')} TODD: translate */}
                      I don&apos;t want advanced cycle tracking
                    </strong>
                  </span>
                </button>

                <LearnMoreButton onClick={openCnoPro}>
                  Learn more
                </LearnMoreButton>
              </PowerPlugSubsection>

              <Separator />
            </Fragment>
          ) : null}

          <PowerPlugSubsection
            // title={intl.formatMessage({
            //   id: 'ring.productSelection.powerPlugsSection.heartHealth.title',
            // })}
            title={
              isAfibOrCardio === 'afib'
                ? 'AFib Detection PowerPlug'
                : 'Cardio Adaptibility PowerPlug'
            }
            description={'Protect your heart health with nighttime monitoring.'}
            image="web_v2/powerplugs/icons/heart-health.png"
          >
            {country &&
            AfibCountryCodes.includes(country) &&
            hasAfibVariants(store as ShopifyStore) ? (
              <Fragment>
                {/* Heart Health */}
                <LargePromptBtnStyledContent
                  className={`ultrahumanx gtm ${
                    afibCart && afibCart === 'Year1' ? 'active' : ''
                  }`}
                  data-buttontype="afib"
                  data-product="Diesel Ultrahuman Ring"
                  data-testid={`ring-addon-1-year`}
                  onClick={() => {
                    setAfibCart('Year1');
                  }}
                  disabled={
                    size &&
                    productMap_diesel_ring[color as DieselRingColors]?.[size]
                      ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                      ? false
                      : true
                  }
                >
                  <div className="content-wrapper">
                    <div className="content">
                      <div className="title-bar">
                        <div className="title">
                          <span>
                            <strong>1 Year</strong>
                          </span>
                        </div>
                        <div className="disclaimer">
                          <div className="price">
                            {currencySymbol}
                            {formatPriceWithComma(
                              AfibShopifyMap[store as ShopifyStore].Year1.price,
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </LargePromptBtnStyledContent>
                <LargePromptBtnStyledContent
                  className={`ultrahumanx gtm ${
                    afibCart && afibCart === 'Year2' ? 'active' : ''
                  } paddingTop`}
                  data-buttontype="afib "
                  data-product="Diesel Ultrahuman Ring"
                  data-testid={`ring-addon-2-year`}
                  onClick={() => {
                    setAfibCart('Year2');
                  }}
                  disabled={
                    size &&
                    productMap_diesel_ring[color as DieselRingColors]?.[size]
                      ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                      ? false
                      : true
                  }
                >
                  <div className="label">recommended</div>
                  <div className="content-wrapper">
                    <div className="content">
                      <div className="title-bar">
                        <div className="title">
                          <span>
                            <strong>2 Years</strong>
                          </span>
                          <span className="save-tag">
                            Save{' '}
                            {AfibShopifyMap[store as ShopifyStore].Year2.save}%
                          </span>
                        </div>
                        <div className="disclaimer">
                          <div className="price">
                            {currencySymbol}
                            {formatPriceWithComma(
                              AfibShopifyMap[store as ShopifyStore].Year2.price,
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </LargePromptBtnStyledContent>
                <button
                  className={`no-ultrahumanx gtm ${
                    afibCart === false ? 'active' : ''
                  }`}
                  data-buttontype="uhx selection"
                  data-product="Diesel Ultrahuman Ring"
                  onClick={() => {
                    setAfibCart(false);
                  }}
                  disabled={
                    size &&
                    productMap_diesel_ring[color as DieselRingColors]?.[size]
                      ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                      ? false
                      : true
                  }
                >
                  <span>
                    <strong>
                      I don&apos;t want proactive heart monitoring
                    </strong>
                  </span>
                </button>
                <LearnMoreButton onClick={openAfibModal}>
                  Learn more
                </LearnMoreButton>
              </Fragment>
            ) : (
              <Fragment>
                {/* Heart Health */}
                <LargePromptBtnStyledContent
                  className={`ultrahumanx gtm ${
                    cardioAdaptibilityCart && cardioAdaptibilityCart === 'Year1'
                      ? 'active'
                      : ''
                  }`}
                  data-buttontype="afib "
                  data-product="Diesel Ultrahuman Ring"
                  data-testid={`ring-addon-1-year`}
                  onClick={() => {
                    setCardioAdaptabilityCart('Year1');
                  }}
                  disabled={
                    size &&
                    productMap_diesel_ring[color as DieselRingColors]?.[size]
                      ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                      ? false
                      : true
                  }
                >
                  <div className="content-wrapper">
                    <div className="content">
                      <div className="title-bar">
                        <div className="title">
                          <span>
                            <strong>1 Year</strong>
                          </span>
                        </div>
                        <div className="disclaimer">
                          <div className="price">
                            {currencySymbol}
                            {formatPriceWithComma(
                              CardioAdaptibilityShopifyMap[
                                store as ShopifyStore
                              ]?.Year1.price,
                            )}
                          </div>
                        </div>
                      </div>
                      {store === ShopifyStore.US ? (
                        <>
                          <div className="hsa-fsa-badge">
                            <FSAHSAComponent />
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                </LargePromptBtnStyledContent>
                <LargePromptBtnStyledContent
                  className={`ultrahumanx gtm ${
                    cardioAdaptibilityCart && cardioAdaptibilityCart === 'Year2'
                      ? 'active'
                      : ''
                  } paddingTop`}
                  data-buttontype="afib "
                  data-product="Diesel Ultrahuman Ring"
                  data-testid={`ring-addon-2-year`}
                  onClick={() => {
                    setCardioAdaptabilityCart('Year2');
                  }}
                  disabled={
                    size &&
                    productMap_diesel_ring[color as DieselRingColors]?.[size]
                      ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                      ? false
                      : true
                  }
                >
                  <div className="label">recommended</div>
                  <div className="content-wrapper">
                    <div className="content">
                      <div className="title-bar">
                        <div className="title">
                          <span>
                            <strong>2 Years</strong>
                          </span>
                          <span className="save-tag">
                            Save{' '}
                            {
                              CardioAdaptibilityShopifyMap[
                                store as ShopifyStore
                              ]?.Year2.save
                            }
                            %
                          </span>
                        </div>
                        <div className="disclaimer">
                          <div className="price">
                            {currencySymbol}
                            {formatPriceWithComma(
                              CardioAdaptibilityShopifyMap[
                                store as ShopifyStore
                              ]?.Year2.price,
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </LargePromptBtnStyledContent>
                <button
                  className={`no-ultrahumanx gtm ${
                    cardioAdaptibilityCart === false ? 'active' : ''
                  }`}
                  data-buttontype="uhx selection"
                  data-product="Diesel Ultrahuman Ring"
                  onClick={() => {
                    setCardioAdaptabilityCart(false);
                  }}
                  disabled={
                    size &&
                    productMap_diesel_ring[color as DieselRingColors]?.[size]
                      ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                      ? false
                      : true
                  }
                >
                  <span>
                    <strong>
                      I don&apos;t want proactive heart monitoring
                    </strong>
                  </span>
                </button>
                <LearnMoreButton onClick={openCardioModal}>
                  Learn more
                </LearnMoreButton>
              </Fragment>
            )}
          </PowerPlugSubsection>
        </Fragment>
      </Section>
    );

    const UHXSelectionSection = !uhx ? (
      <Section
        // title="Add an UltrahumanX coverage plan"
        title={'UltrahumanX Coverage.'}
        // subText="Your peace of mind is precious. Get accidental damage protection and coverage for your Ring AIR."
        subTitle={'Protect your new Ring.'}
        childClass="ultrahumanx-section"
        show={!!size}
        ref={coverageRef}
        openInfoModal={openUhxInfoModal}
      >
        <div className="ultrahumanx-content">
          <LargePromptBtnStyledContent
            className={`ultrahumanx gtm ${uhxCart === 'Year1' ? 'active' : ''}`}
            data-buttontype="uhx selection 1 year"
            data-product="Diesel Ultrahuman Ring"
            data-testid={`ring-uhx-1-year`}
            onClick={() => {
              setUhxCart('Year1');
            }}
            disabled={
              size &&
              productMap_diesel_ring[color as DieselRingColors]?.[size]
                ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                ? false
                : true
            }
          >
            <div className="content-wrapper">
              <div className="content">
                <div className="title-bar">
                  <div className="title">
                    <span>
                      <strong>{/* 1 Year Coverage */}1 Year Coverage</strong>
                    </span>
                  </div>
                  <div className="disclaimer">
                    <div className="price">
                      {/* CONFIG: X days campaign - remove ☝🏻 and strike-through class */}
                      {currencySymbol}
                      {formatPriceWithComma(
                        UhxShopifyMap[store as ShopifyStore]?.Year1.price,
                      )}
                    </div>
                  </div>
                </div>
                <Separator transparent size="sm" />
                <div className="list">
                  <p>
                    <Checkmark className="checkmark" fill="#000000" />
                    {/* Accidental
                        damage protection */}
                    Accidental damage protection
                  </p>
                  <p>
                    <Checkmark className="checkmark" fill="#000000" />
                    {/* Priority care */}
                    Priority care
                  </p>
                </div>
              </div>
            </div>
          </LargePromptBtnStyledContent>
          <LargePromptBtnStyledContent
            className={`ultrahumanx gtm ${
              uhxCart === 'Year2' ? 'active' : ''
            } paddingTop`}
            data-buttontype="uhx selection 2 year"
            data-product="Diesel Ultrahuman Ring"
            data-testid={`ring-uhx-2-year`}
            onClick={() => {
              setUhxCart('Year2');
            }}
            disabled={
              size &&
              productMap_diesel_ring[color as DieselRingColors]?.[size]
                ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                ? false
                : true
            }
          >
            <div className="label">Popular</div>
            <div className="content-wrapper">
              <div className="content">
                <div className="title-bar">
                  <div className="title">
                    <span>
                      <strong>{/* 2 Year Coverage */}2 Year Coverage</strong>
                    </span>
                    <span className="save-tag">
                      {/* Save{' '} */}
                      Save{' '}
                      {Math.floor(
                        ((UhxShopifyMap[store as ShopifyStore]?.Year1.price -
                          UhxShopifyMap[store as ShopifyStore]?.Year2.price /
                            2) /
                          UhxShopifyMap[store as ShopifyStore]?.Year1.price) *
                          100,
                      )}
                      %
                    </span>
                  </div>
                  <div className="disclaimer">
                    <div className="price">
                      {/* CONFIG: X days campaign - remove ☝🏻 and strike-through class */}
                      {currencySymbol}
                      {formatPriceWithComma(
                        UhxShopifyMap[store as ShopifyStore]?.Year2.price,
                      )}
                    </div>
                    <div className="disclaimer-text">
                      <span>
                        {/* (That's just {currencySymbol} */}
                        (That&apos;s just {currencySymbol}
                        {formatPriceWithComma(
                          UhxShopifyMap[store as ShopifyStore]?.Year2.price / 2,
                        )}
                        /year)
                      </span>
                    </div>
                  </div>
                </div>
                <Separator transparent size="sm" />
                <div className="list">
                  <p>
                    <Checkmark className="checkmark" fill="#000000" />
                    {/* Includes all 1
                        Year Coverage benefits */}
                    Includes all 1 Year Coverage benefits
                  </p>
                  <p>
                    <Checkmark className="checkmark" fill="#000000" />
                    {/* Theft and loss
                        protection */}
                    Theft and loss protection
                  </p>
                  <p>
                    <Checkmark className="checkmark" fill="#000000" />
                    {/* Weight loss
                        insurance */}
                    Weight loss insurance
                  </p>
                </div>
              </div>
            </div>
          </LargePromptBtnStyledContent>
          <button
            className={`no-ultrahumanx gtm ${
              uhxCart === false ? 'active' : ''
            }`}
            data-buttontype="uhx selection"
            data-product="Diesel Ultrahuman Ring"
            onClick={() => {
              setUhxCart(false);
            }}
            disabled={
              size &&
              productMap_diesel_ring[color as DieselRingColors]?.[size]
                ?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                ? false
                : true
            }
          >
            <span>
              <strong>
                {/* No, I don't want to protect my new Ring AIR */}I don&apos;t
                want to protect my new Diesel Ultrahuman Ring
              </strong>
            </span>
          </button>
          {/* <div className="footer-info">
                //*Already an UltrahumanX subscriber? Log in with your
                registered email to access your benefits.
                {t('ring.productSelection.uhxSectionNew.tnc')}
              </div> */}
        </div>
      </Section>
    ) : (
      <Section childClass="ultrahumanx-section" show={!!size}>
        <div className="ultrahumanx-content">
          <div className={`card ultrahumanx`}>
            <div className="uhx-content">
              <div className="content">
                <h5>UltrahumanX subscription applied</h5>
              </div>
            </div>
          </div>
        </div>
      </Section>
    );

    const OverviewSection =
      notServicable === true ? (
        <Fragment>{NotServicableMessage}</Fragment>
      ) : (
        <Fragment>
          {size ? (
            <Section
              childClass="cart-summary"
              show={!!size && (uhx || uhxCart !== null)}
              ref={cartSummaryRef}
            >
              <div className="card" id="order-summary-card">
                {/* <h4>Almost there. Here's your order summary.</h4> */}
                <h4>Almost there. Here&apos;s your order summary.</h4>

                {effectiveDiscount.type === 'hero' ? (
                  <div
                    className="disclaimer"
                    style={{
                      marginBottom: '20px',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: '-16px',
                    }}
                  >
                    <InfoIcon
                      fill="#00000077"
                      style={{
                        flexShrink: '0',
                        marginRight: '8px',
                        alignSelf: 'flex-start',
                        transform: 'translateY(2px)',
                      }}
                    />
                    <p>
                      Heroes Special Pricing Verification: By purchasing, you
                      consent to provide ID or proof of eligibility if
                      requested. We collect only what&apos;s needed, delete it
                      after use, and handle your data per applicable privacy
                      laws. Please review our{' '}
                      <CustomLink
                        href="/privacyPolicy"
                        target="_blank"
                        style={{
                          color: 'inherit !important',
                          textDecoration: 'underline',
                        }}
                      >
                        Privacy Policy
                      </CustomLink>{' '}
                      for details.
                    </p>
                    {/* Heroes Special Pricing Verification: By purchasing, you consent
                to provide ID or proof of eligibility if requested. We collect
                only what&apos;s needed, delete it after use, and handle your
                data per applicable privacy laws. Please review our Privacy
                Policy for details */}
                  </div>
                ) : null}

                <div className="content">
                  <h5>Diesel Ultrahuman Ring</h5>

                  {size ? (
                    <p className="size">
                      {color ? DieselRingColorNameImages[color].name : ''},{' '}
                      {size === DieselRingSizes.OPEN
                        ? // 'Sizing kit included'
                          'Sizing kit included'
                        : //  ` Size ${size}`
                          `Size ${size}`}
                    </p>
                  ) : null}
                  <div className="price-breakup">
                    <p>
                      {/* Ring AIR */}
                      Diesel Ultrahuman Ring{' '}
                    </p>
                    <p>
                      {currencySymbol}
                      {formatPriceWithComma(price)}
                    </p>
                  </div>
                  {uhxCart ? (
                    <div className="price-breakup">
                      <p>
                        {uhxCart === 'Year2'
                          ? //  'UltrahumanX - 2 Years'
                            'UltrahumanX - 2 Years'
                          : //  'UltrahumanX - 1 Year'
                            'UltrahumanX - 1 Year'}
                      </p>
                      <p>
                        {ultraHumanPrice === 0 ? (
                          <>
                            {/* FREE */}
                            FREE
                          </>
                        ) : (
                          <>
                            +{currencySymbol}
                            {formatPriceWithComma(ultraHumanPrice)}
                          </>
                        )}
                      </p>
                    </div>
                  ) : null}
                  {afibCart ? (
                    <div className="price-breakup">
                      <p>AFib Detection PowerPlug</p>
                      <p>
                        +{currencySymbol}
                        {formatPriceWithComma(
                          AfibShopifyMap[store as ShopifyStore][afibCart].price,
                        )}
                      </p>
                    </div>
                  ) : null}
                  {cardioAdaptibilityCart ? (
                    <div className="price-breakup">
                      <p>Cardio Adaptibility PowerPlug</p>
                      <p>
                        +{currencySymbol}
                        {formatPriceWithComma(
                          CardioAdaptibilityShopifyMap[store as ShopifyStore][
                            cardioAdaptibilityCart
                          ].price,
                        )}
                      </p>
                    </div>
                  ) : null}
                  {isClueFlow && clueCart ? (
                    <div className="price-breakup">
                      <p>Clue Plus - 1 Year</p>
                      <p>
                        {currencySymbol}
                        {formatPriceWithComma(
                          ClueShopifyMap[store as ShopifyStore]?.Year1?.price ||
                            0,
                        )}
                      </p>
                    </div>
                  ) : null}
                  {respiratoryCart ? (
                    <div className="price-breakup">
                      <p>Respiratory Health PowerPlug - 1 Year</p>
                      <p>
                        {currencySymbol}
                        {formatPriceWithComma(
                          RespiratoryShopifyMap[store as ShopifyStore]?.Year1
                            ?.price || 0,
                        )}
                      </p>
                    </div>
                  ) : null}
                  {cnoProCart ? (
                    <div className="price-breakup">
                      <p>
                        {cnoProCart === 'Year2'
                          ? 'Cycle & Ovulation Pro PowerPlug - 2 years'
                          : 'Cycle & Ovulation Pro PowerPlug - 1 year'}
                      </p>
                      <p>
                        +{currencySymbol}
                        {formatPriceWithComma(
                          CnoProShopifyMap[store as ShopifyStore][cnoProCart]
                            .price,
                        )}
                      </p>
                    </div>
                  ) : null}
                  {/* {chargerType === RingChargerType.TRAVEL_CHARGER ? (
                      <div className="price-breakup">
                        <p>Voyager Ring Charger</p>
                        <p>
                          +{currencySymbol}
                          {formatPriceWithComma(chargerPrice)}
                        </p>
                      </div>
                    ) : null} */}
                  {affiliateDiscount &&
                  effectiveDiscount.type === 'affiliate' ? (
                    <div className="price-breakup">
                      <p>
                        {/* Affiliate discount */}
                        Affiliate discount
                      </p>
                      <p>
                        -{currencySymbol}
                        {formatPriceWithComma(affiliateDiscount)}
                      </p>
                    </div>
                  ) : null}
                  {referral && effectiveDiscount.type === 'referral' ? (
                    <div className="price-breakup">
                      <p>
                        {/* Affiliate discount */}
                        Affiliate discount
                      </p>
                      <p>
                        -{currencySymbol}
                        {formatPriceWithComma(referral.discount)}
                      </p>
                    </div>
                  ) : null}
                  {effectiveDiscount.type === 'hero' ? (
                    <div className="price-breakup">
                      <p>
                        {/* Affiliate discount */}
                        Hero Discount
                        <span style={{ display: 'block', fontSize: '1.2rem' }}>
                          You may be asked to provide ID or proof of
                          eligibility.
                        </span>
                      </p>
                      <p>
                        -{currencySymbol}
                        {formatPriceWithComma(effectiveDiscount.amount)}
                      </p>
                    </div>
                  ) : null}
                  {isClueFlow && clueCart ? (
                    <div className="price-breakup">
                      <p>Ultrahuman x Clue Plus Offer</p>
                      <p>
                        -{currencySymbol}
                        {formatPriceWithComma(
                          (ClueShopifyMap[store as ShopifyStore]?.Year1
                            ?.price || 0) -
                            (ClueShopifyMap[store as ShopifyStore]?.Year1
                              ?.discountedPrice || 0),
                        )}
                      </p>
                    </div>
                  ) : null}
                  {isRespiratoryFlow && respiratoryCart ? (
                    <div className="price-breakup">
                      <p>Respiratory Health PowerPlug Special Offer</p>
                      <p>
                        -{currencySymbol}
                        {formatPriceWithComma(
                          (RespiratoryShopifyMap[store as ShopifyStore]?.Year1
                            ?.price || 0) -
                            (RespiratoryShopifyMap[store as ShopifyStore]?.Year1
                              ?.discountedPrice || 0),
                        )}
                      </p>
                    </div>
                  ) : null}
                  {effectiveDiscount.type === 'birthday' ? (
                    <div className="price-breakup">
                      <p>
                        {/* Birthday discount */}
                        Birthday discount (
                        {Math.round((effectiveDiscount.amount / price) * 100)}
                        %)
                      </p>
                      <p>
                        -{currencySymbol}
                        {formatPriceWithComma(effectiveDiscount.amount)}
                      </p>
                    </div>
                  ) : null}
                  {effectiveDiscount.type === 'ringPromo' ? (
                    <div className="price-breakup">
                      <p>{ringCampaign?.title || 'Special Offer'} Applied</p>
                      <p>
                        -{currencySymbol}
                        {formatPriceWithComma(effectiveDiscount.amount)}
                      </p>
                    </div>
                  ) : null}
                  {!(
                    !(store === ShopifyStore.GB || store === ShopifyStore.EU) &&
                    affiliateDiscount
                  ) &&
                  defaultOfferText &&
                  defaultOfferDiscount ? (
                    <div className="price-breakup">
                      <p>
                        {/* Affiliate discount */}
                        {/* {t('ring.productSelection.cartSummary.priceBreakup.b')} */}
                        {defaultOfferText} offer
                      </p>
                      <p>
                        -{currencySymbol}
                        {formatPriceWithComma(defaultOfferDiscount as number)}
                      </p>
                    </div>
                  ) : null}
                </div>

                {size ? (
                  <div className="total">
                    <div className="total-bar">
                      <h5>
                        {/* Total */}
                        Total{' '}
                      </h5>{' '}
                      <p>
                        {currencySymbol}
                        {formatPriceWithComma(effectivePrice)}
                      </p>
                    </div>
                    {getShippingAndTaxDisclaimerMap(tCart)[
                      store as ShopifyStore
                    ] ? (
                      <p
                        className="taxes"
                        style={{
                          marginBottom: '4px',
                        }}
                      >
                        {
                          getShippingAndTaxDisclaimerMap(tCart)[
                            store as ShopifyStore
                          ]
                        }
                      </p>
                    ) : null}

                    {shippingEstimate ? (
                      <p className="taxes shipping-estimate">
                        {shippingEstimate}
                      </p>
                    ) : null}

                    <PrimaryButton
                      className="checkout"
                      disabled={
                        outOfStock ||
                        isLoading ||
                        !(!!size && (uhx || uhxCart !== null))
                      }
                      aria-busy={isLoading}
                      data-buttontype="add to cart"
                      data-product="Diesel Ultrahuman Ring"
                      data-testid="ring-add-to-cart"
                      data-variantId={
                        color && size && currency
                          ? productMap_diesel_ring[color as DieselRingColors]?.[
                              size
                            ]?.NOT_ENGRAVED?.variantId[store as ShopifyStore]
                          : ''
                      }
                      onClick={handleCheckout}
                    >
                      {isLoading ? (
                        <DotSpinner color="#000000" />
                      ) : outOfStock ? (
                        t('ring.productSelection.cartSummary.soldOut')
                      ) : (
                        t('ring.productSelection.cartSummary.addToCart')
                      )}
                    </PrimaryButton>

                    {/* <div className="" style={{ marginBottom: '16px' }}>
                    <HeroConsentDisclaimer align="flex-start" icon />
                  </div> */}

                    {pathname?.includes('/us/') ? (
                      <div className="hsa-fsa-disclaimer">
                        <CustomImage
                          src="web_v2/icons/hsa-fsa/hsa-fsa-icon-black.svg"
                          alt="HSA/FSA"
                          width="248"
                          height="81"
                        />
                        <span>
                          <b>HSA/FSA</b> Eligible | Save more with your pre-tax
                          dollars
                        </span>
                      </div>
                    ) : null}

                    <div className="shipping-info">
                      <p className="free-delivery">
                        {emiCopies
                          ? `No cost EMI starting at ₹${formatPriceWithComma(
                              RingSelectionClient.effectivePrice / 24,
                            )}/month. Free Delivery`
                          : null}
                      </p>
                    </div>
                  </div>
                ) : null}
                <div className="benefits">
                  <div className="charger benefit">
                    <Charger />
                    <div>
                      <p>
                        {/* Charging cable */}
                        Charging cable included
                      </p>
                    </div>
                  </div>
                  <div className="battery benefit">
                    <Battery />
                    <p>
                      {/* Up to 6 days battery life */}4 to 6 days of battery
                      life
                    </p>
                  </div>
                  <div className="return benefit">
                    <Return />
                    <p>
                      {/* 30-day return */}
                      30-day return
                    </p>
                  </div>
                </div>

                {store &&
                  ![
                    ShopifyStore.IN,
                    ShopifyStore.AE,
                    ShopifyStore.GB,
                    ShopifyStore.EU,
                  ].includes(store as ShopifyStore) && (
                    <p className="disclaimer">
                      {/* *Please note that additional local taxes or carrier fees may
                    apply upon delivery */}
                      *Please note that additional local taxes or carrier fees
                      may apply upon delivery
                    </p>
                  )}
                {/* <PlantTreesBanner /> */}
              </div>
            </Section>
          ) : null}
        </Fragment>
      );

    return (
      <div
        className={
          className ? `${styles.buySection} ${className}` : styles.buySection
        }
      >
        <div className="buy-section title-info-content">
          {/* mobile section */}
          <div className="title-container">
            <H2>{buySectionTitle}</H2>
          </div>
          {notServicable === true ? null : (
            <PriceContentContainer>
              {/* Price listing */}
              <p
                className={`price ${
                  effectiveDiscount.amount ? 'affiliate' : ''
                }`}
              >
                {priceDrop > 0 ||
                defaultStrikedPrice ||
                effectiveDiscount.amount > 0 ? (
                  <>
                    <span className={'strike-through'}>
                      {
                        CurrencySymbol[
                          RingSelectionClient.defaultCurrency.toLowerCase() as Currency
                        ]
                      }
                      {formatPriceWithComma(strikeBasePrice)}
                    </span>
                  </>
                ) : null}
                <span>
                  {
                    CurrencySymbol[
                      RingSelectionClient.defaultCurrency.toLowerCase() as Currency
                    ]
                  }
                  {formatPriceWithComma(price - effectiveDiscount.amount)}
                </span>
              </p>
              {/* <span className="early-bird-chip">(Free Shipping)</span> */}
              <div
                className="offerFlex"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px',
                  width: '100%',
                }}
              >
                {effectiveDiscount.type === 'affiliate' ? (
                  <p className="offerTag transparent">
                    <span className=" ">
                      {Number(
                        affiliateDetails?.discount_configs[
                          effectiveStoreForAffiliate as ShopifyStore
                        ]?.RING_DIESEL?.value,
                      )}
                      % off applied via{' '}
                      {
                        affiliateDetails?.discount_configs[
                          effectiveStoreForAffiliate as ShopifyStore
                        ]?.RING_DIESEL?.code
                      }
                      {/* work affiliate */}
                      {searchParams?.get('workAffiliate') ? ' ultrahuman' : ''}
                    </span>
                  </p>
                ) : null}{' '}
                {effectiveDiscount.type === 'referral' && referral ? (
                  <p className="offerTag">
                    <span>
                      <span className="offerApplied">
                        {referral.discountCode}
                      </span>{' '}
                      Offer Applied
                    </span>
                  </p>
                ) : null}
                {effectiveDiscount.type === 'hero' &&
                heroCategoryOfferTagMap[
                  searchParams?.get('heroCategory') as string
                ] ? (
                  <p className="offerTag transparent">
                    <span>
                      {
                        heroCategoryOfferTagMap[
                          searchParams?.get('heroCategory') as string
                        ]
                      }
                    </span>
                  </p>
                ) : null}
                {isClueFlow && country && store ? (
                  <p
                    className="offerTag"
                    style={{
                      width: '100%',
                      flexWrap: 'nowrap',
                    }}
                  >
                    <span>
                      <ClueLogo />
                      Includes 1 Year Clue Plus {currencySymbol}
                      {
                        ClueShopifyMap[store as ShopifyStore].Year1
                          .discountedPrice
                      }{' '}
                      (worth {currencySymbol}
                      {ClueShopifyMap[store as ShopifyStore].Year1.price})
                    </span>
                  </p>
                ) : null}
                {isRespiratoryFlow && country && store ? (
                  <p
                    className="offerTag"
                    style={{
                      width: '100%',
                      flexWrap: 'nowrap',
                    }}
                  >
                    <span>
                      <RespiratoryLogo />
                      Includes 1 Year Respiratory Health Powerplug FREE (worth{' '}
                      {currencySymbol}
                      {RespiratoryShopifyMap[store as ShopifyStore].Year1.price}
                      )
                    </span>
                  </p>
                ) : null}
                {isOvusenseFlow && country && store && isCnoProActive ? (
                  <button
                    onClick={openCnoPro}
                    className="offerTag"
                    style={{
                      width: '100%',
                      flexWrap: 'nowrap',
                    }}
                  >
                    <CustomImage
                      src="web_v2/icons/cno-pro-addon-small.png"
                      alt="cno-pro-addon-small"
                      width="24"
                      height="24"
                    />
                    <span>
                      {/* <ClueLogo /> */}
                      Includes 1 Year{' '}
                      {getValue(
                        'Cycle & Ovulation Pro',
                        'Cycle & Ovulation Plus',
                      )}{' '}
                      (worth {currencySymbol}
                      {CnoProShopifyMap[store as ShopifyStore].Year1.price}
                      )
                      <ChevronRight
                        style={{
                          width: 'auto',
                          height: '12px',
                          paddingLeft: '4px',
                          flexShrink: '0',
                        }}
                      />
                    </span>
                  </button>
                ) : null}
                {effectiveDiscount.type === 'birthday' ? (
                  <p className="offerTag">
                    <Image
                      src={BirthdayIcon}
                      alt="birthday_icon"
                      style={{
                        borderRadius: '0',
                      }}
                    />
                    <span>
                      {/* <span className="offerApplied">Your birthday</span>{' '} */}
                      Birthday Discount Applied
                    </span>
                  </p>
                ) : null}
                {effectiveDiscount.type === 'ringPromo' ? (
                  <p className="offerTag transparent">
                    <span>
                      {ringCampaign?.title || 'Special'} Offer Applied
                    </span>
                  </p>
                ) : null}
                {defaultOfferText && !(affiliateDetails || referral) ? (
                  <p className="offerTag">
                    <span>
                      <span className="offerApplied">{defaultOfferText}</span>{' '}
                      Offer Applied
                    </span>
                  </p>
                ) : null}
              </div>
              {outOfStock ? <OutOfStockBanner /> : <LimitedEditionBanner />}
              {/* {color ? (
                <p className="shipping-estimate">
                  {color ? (
                    shippingEstimate
                  ) : (
                    <>Dispatch time based on ring selection</>
                  )}
                </p>
              ) : null} */}
              {/* {!offersData ? (
                <p className="description">
                  //A comfortable and sleek smart ring that tracks your sleep, movement and recovery trends, nudging you to make healthier choices every day.
                  {description}
                </p>
              ) : null} */}
              {/* <ViewCount /> */}
              {secondaryOfferData && (
                <OffersCarousel data={secondaryOfferData} leftAligned={true} />
              )}
              {/* {secondaryOfferData && (
            <SecondaryOfferComponent data={secondaryOfferData} />
          )} */}
            </PriceContentContainer>
          )}
        </div>

        <MobileCarouselSection className="carousel-section">
          <Carousal
            // selectedColor={RingSelectionClient.color as DieselRingColors}
            items={
              (RingSelectionClient.color
                ? DieselRingCarouselData[RingSelectionClient.color]
                : DieselRingCarouselData[DieselRingColors.CRYO]) ?? []
            }
          />
        </MobileCarouselSection>

        {/* Color */}
        {ColorSelectionSection}

        {/* Size */}
        {SizeSelectionSection}

        {/* charger section - NO CHARGER SECTION HERE */}
        {/* <Section
          title={
            "Charger."
          }
          subTitle={
            "Power up and never miss a beat."
          }
          childClass="charger-section"
          show={!!size}
          disableScrollToScreen={true}
          sectionClass="charger-section"
          ref={showChargerRef}
          openInfoModal={openVoyagerCharger}
          // hsaFsaBadge={true}
        >
          <>
            <div className="charger">
              <button
                className={`primary ${
                  chargerType === RingChargerType.TRAVEL_CHARGER ? 'active' : ''
                }`}
                onClick={() => {
                  setChargerType(RingChargerType.TRAVEL_CHARGER);
                }}
                disabled={!size}
              >
                <CustomImage
                  src="web_v2/illustrations/voyager_charger_thumb.png/w_800"
                  alt="Travel charger"
                  width={200}
                  height={150}
                  style={{ marginBottom: '1.1rem', objectFit: 'contain' }}
                />
                <span className="tag">NEW</span>
                {store === ShopifyStore.US ? (
                  <div
                    className="hsa-fsa-badge"
                    style={{ position: 'absolute', top: '0px', right: '4px' }}
                  >
                    <FSAHSAComponent />
                  </div>
                ) : null}
                <p className="title">
                  Get the Voyager Ring Charger at {' '}
                  {
                    CurrencySymbol[
                      RingSelectionClient.defaultCurrency.toLowerCase() as Currency
                    ]
                  }
                  {store && (
                    <>
                      {formatPriceWithComma(
                        TravelChargerShopifyMap[store].price as number,
                      )}
                    </>
                  )}
                </p>
                <p className="sub-title">
                  A compact, powerful, and lightweight charger, engineered for the adventurer in you.
                </p>
              </button>

              <button
                className={`secondary ${
                  RingSelectionClient.chargerType === RingChargerType.OG
                    ? 'active'
                    : ''
                }`}
                onClick={() => {
                  setChargerType(RingChargerType.OG);
                }}
                disabled={!size}
              >
                Free standard charger
              </button>
            </div>
          </>
        </Section> */}

        {/* ENGRAVING - No Engraving */}

        {/* Clue Addon */}
        {ClueAddonSection}

        {/* DISABLED: Respiratory Addon — re-enable by uncommenting */}
        {/* {RespiratoryAddonSection} */}

        {/* PowerPlugs */}
        {PowerplugsSelectionSection}

        {/* UHX */}
        {UHXSelectionSection}

        {/* {effectiveDiscount.type === 'hero' ? (
          <Section
            childClass="cart-summary"
            ref={heroDisclaimerRef}
            show={!!size && (uhx || uhxCart !== null)}
          >
            <div className="card">
            </div>
          </Section>
        ) : null} */}

        {/* Overview */}
        {OverviewSection}
      </div>
    );
};
